import * as React from 'react';
import { useState } from 'react';

import type { NotificationProps, NotificationType } from '@coursera/coursera-ui';
import { Notification } from '@coursera/coursera-ui';

import 'css!./__styles__/Notifications';

export type NotificationParam = string | Error | NotificationProps;

type ContextType = {
  notificationQueue: NotificationQueue;
  notify: (newNotif: NotificationParam, type?: NotificationType) => void;
};

export const NotificationsContext = React.createContext<ContextType>({
  notificationQueue: [],
  notify: (newNotif: NotificationParam, type?: NotificationType) => {
    /* Empty function */
  },
});

export const useNotifications = () => {
  return React.useContext(NotificationsContext);
};

type NotificationQueue = Array<NotificationProps & { key: number }>;

type Props = {
  /** Children of this component will be able to access the values passed through NotificationsContext.Provider */
  children?: React.ReactNode;
  /** Number of milliseconds to keep a notifcation open. Default is 5000. */
  msBeforeNotificationClose?: number;
  /** Number of notifications visible at one time on the screen. Default is 1. */
  numberOfNotificationsVisible?: number;
};

export const Notifications = ({
  children,
  msBeforeNotificationClose = 5000,
  numberOfNotificationsVisible = 1,
}: Props) => {
  const [notificationQueue, setNotificationQueue] = useState<NotificationQueue>([]);

  const notify = (notification: NotificationParam, type?: NotificationType) => {
    let newNotif;
    const timestamp = Date.now();

    const _dismiss = (dismissTimestamp: $TSFixMe) => {
      setNotificationQueue((prevQueue) => [...prevQueue].filter((notif) => notif.key !== dismissTimestamp));
    };

    const defaultProps = {
      key: timestamp,
      type: type || 'info',
      isDismissible: true,
      onDismiss: () => _dismiss(timestamp),
      isTransient: !!msBeforeNotificationClose,
      transientDuration: msBeforeNotificationClose,
    };

    if (typeof notification === 'string') {
      newNotif = {
        ...defaultProps,
        message: notification,
      };
    } else if (notification instanceof Error) {
      newNotif = {
        ...defaultProps,
        message: notification.message,
        type: 'danger',
      };
    } else if ((notification as NotificationProps).type) {
      newNotif = {
        ...defaultProps,
        ...notification,
      };
    }
    // @ts-expect-error ts-migrate(2322) FIXME: Type '(NotificationProps & { key: number; }) | { m... Remove this comment to see the full error message
    setNotificationQueue([...notificationQueue, newNotif]);
  };

  return (
    <NotificationsContext.Provider value={{ notificationQueue, notify }}>
      <div className="rc-Notifications">
        {notificationQueue
          .slice(0, numberOfNotificationsVisible)
          .reverse()
          .map((notif) => (
            <Notification {...notif} />
          ))}
      </div>
      {children}
    </NotificationsContext.Provider>
  );
};

export default Notifications;
